import { useState } from "react";
import { X, EnvelopeSimple, Check, CircleNotch, DownloadSimple } from "@phosphor-icons/react";
import axios from "axios";
import { API, trackEvent } from "@/App";

const MODAL_COPY = {
  "cheat-sheet": {
    title: "GET THE CHEAT SHEET",
    desc: "Enter your email and we'll send you the Dynasty Startup Draft Cheat Sheet (PDF) instantly.",
    button: "SEND ME THE PDF",
  },
  "championship-report-pdf": {
    title: "GET THE FULL REPORT",
    desc: "Download the complete championship analysis — printable, shareable, and formatted for easy reference.",
    button: "DOWNLOAD THE REPORT",
  },
};

export default function EmailCaptureModal({ open, onClose, source = "cheat-sheet" }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null); // null | 'loading' | 'success' | 'duplicate' | 'error'
  const [message, setMessage] = useState("");
  const [downloadUrl, setDownloadUrl] = useState("");

  if (!open) return null;

  const copy = MODAL_COPY[source] || MODAL_COPY["cheat-sheet"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/leads/capture`, { email: email.trim(), source });
      if (res.data.status === "ok") {
        setStatus("success");
        setMessage(res.data.message);
        setDownloadUrl(res.data.download_url || "");
        trackEvent("lead_captured", source);
      } else if (res.data.status === "already_subscribed") {
        setStatus("duplicate");
        setMessage(res.data.message);
        setDownloadUrl(res.data.download_url || "");
      }
    } catch (e) {
      setStatus("error");
      setMessage("Something went wrong. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative bg-[#171717] border border-[#262626] w-full max-w-md p-6 sm:p-8" onClick={(e) => e.stopPropagation()} data-testid="email-capture-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#666] hover:text-white" data-testid="close-email-modal">
          <X size={18} />
        </button>

        {status === "success" || status === "duplicate" ? (
          <div className="text-center py-4">
            <div className="w-12 h-12 bg-[#ccff00]/10 border border-[#ccff00]/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={24} className="text-[#ccff00]" weight="bold" />
            </div>
            <h3 className="font-heading text-xl text-white uppercase mb-2" data-testid="email-success-title">
              {status === "success" ? "YOU'RE IN" : "ALREADY ON THE LIST"}
            </h3>
            <p className="text-sm text-[#a3a3a3] mb-4">{message}</p>
            {downloadUrl ? (
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 h-10 px-6 bg-[#ccff00] text-black rounded-sm text-[11px] font-bold uppercase no-underline hover:bg-[#b8e600] transition-colors"
                data-testid="pdf-download-link"
              >
                <DownloadSimple size={14} weight="bold" /> DOWNLOAD PDF NOW
              </a>
            ) : (
              <button onClick={onClose} className="mt-2 h-9 px-6 btn-primary rounded-sm text-[11px] font-bold uppercase" data-testid="email-done-btn">GOT IT</button>
            )}
            <p className="text-[10px] text-[#555] mt-3">{"We've also sent the link to"} <strong className="text-white">{email}</strong></p>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="w-10 h-10 bg-[#ccff00]/10 border border-[#ccff00]/30 rounded-sm flex items-center justify-center mx-auto mb-3">
                <EnvelopeSimple size={20} className="text-[#ccff00]" weight="bold" />
              </div>
              <h3 className="font-heading text-xl text-white uppercase mb-1">{copy.title}</h3>
              <p className="text-xs text-[#888]">{copy.desc}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="w-full h-11 px-4 bg-[#0a0a0a] border border-[#262626] text-white text-sm rounded-sm placeholder:text-[#555] focus:border-[#ccff00] focus:outline-none transition-colors"
                data-testid="email-input"
                autoFocus
              />
              <button
                type="submit"
                disabled={status === "loading" || !email.trim()}
                className="w-full h-11 btn-primary rounded-sm text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="email-submit-btn"
              >
                {status === "loading" ? (
                  <><CircleNotch size={14} className="animate-spin" /> SENDING...</>
                ) : (
                  copy.button
                )}
              </button>
              {status === "error" && <p className="text-xs text-[#ff3b30] text-center">{message}</p>}
            </form>

            <p className="text-[9px] text-[#444] text-center mt-4">No spam. Just the PDF and occasional dynasty tips.</p>
          </>
        )}
      </div>
    </div>
  );
}
